/**
 * Offline Queue System for Stocked & Loaded
 * 
 * Queues failed write operations for later sync when connectivity returns.
 * Uses IndexedDB for persistence across sessions and page reloads.
 * 
 * HARDENING:
 * - Queue persists across reload/navigation
 * - Items only removed on successful 2xx response
 * - Failed syncs show error state in UI
 * - Token refresh failure pauses sync without clearing queue
 */

import { v4 as uuidv4 } from 'uuid';

const DB_NAME = 'StockedLoadedOffline';
const DB_VERSION = 1;
const STORE_NAME = 'pendingActions';

// Queue status enum
export const QueueStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  FAILED: 'failed',
};

// Action types we queue
export const QueueableActions = [
  '/inventory/restock',
  '/inventory/use',
  '/transfers',
  '/adjustment-requests',
  '/messages',
];

// Actions we NEVER queue
const NON_QUEUEABLE_PATTERNS = [
  '/auth/',
  '/inventory/parse-csv',
  '/inventory/validate',
  '/inventory/execute-import',
  '/items/export',
  '/export.csv',
  '/bulk/',
  '/categories',
];

class OfflineQueueManager {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.lastSyncError = null;  // Track last sync error for UI
    this.loginRequired = false; // Track if login is required to sync
    this.listeners = new Set();
    this.backoffDelay = 5000; // Start with 5 seconds
    this.maxBackoffDelay = 30000; // Max 30 seconds
    
    this.init();
  }

  async init() {
    await this.openDatabase();
    this.setupNetworkListeners();
    
    // Load initial state immediately (don't auto-sync yet)
    await this.notifyListeners();
    
    // Auto-sync on load if online (but don't block init)
    if (this.isOnline) {
      setTimeout(() => this.syncQueue(), 2000);
    }
  }

  openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('user_id', 'user_id', { unique: false });
          store.createIndex('tenant_id', 'tenant_id', { unique: false });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('created_at', 'created_at', { unique: false });
        }
      };
    });
  }

  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.lastSyncError = null;
      this.notifyListeners();
      // Auto-sync when coming back online
      this.syncQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners();
    });
  }

  // Subscribe to queue changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  async notifyListeners() {
    const state = await this.getQueueState();
    this.listeners.forEach(callback => callback(state));
  }

  async getQueueState() {
    const items = await this.getAllItems();
    const pendingCount = items.filter(i => i.status === QueueStatus.PENDING).length;
    const failedCount = items.filter(i => i.status === QueueStatus.FAILED).length;
    const syncingCount = items.filter(i => i.status === QueueStatus.SYNCING).length;
    
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      lastSyncError: this.lastSyncError,
      loginRequired: this.loginRequired,
      pendingCount,
      failedCount,
      syncingCount,
      totalCount: items.length,
      items,
    };
  }

  // Check if an action should be queued
  shouldQueue(method, url) {
    // Only queue write operations
    if (!['POST', 'PUT', 'DELETE'].includes(method.toUpperCase())) {
      return false;
    }
    
    // Check if URL matches non-queueable patterns
    for (const pattern of NON_QUEUEABLE_PATTERNS) {
      if (url.includes(pattern)) {
        return false;
      }
    }
    
    // Check if URL matches queueable patterns
    for (const pattern of QueueableActions) {
      if (url.includes(pattern)) {
        return true;
      }
    }
    
    return false;
  }

  // Get action type label for display
  getActionLabel(url, method) {
    if (url.includes('/inventory/restock')) return 'Restock';
    if (url.includes('/inventory/use')) return 'Use/Consume';
    if (url.includes('/transfers')) return 'Transfer';
    if (url.includes('/adjustment-requests') && url.includes('/approve')) return 'Approve Adjustment';
    if (url.includes('/adjustment-requests') && url.includes('/reject')) return 'Reject Adjustment';
    if (url.includes('/adjustment-requests')) return 'Request Adjustment';
    if (url.includes('/messages')) return 'Send Message';
    return method.toUpperCase();
  }

  // Add item to queue
  async enqueue(method, url, body, headers = {}) {
    if (!this.db) await this.openDatabase();
    
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    const item = {
      id: uuidv4(),
      created_at: new Date().toISOString(),
      method: method.toUpperCase(),
      url,
      body,
      // Store only safe headers (no auth token - will be added at sync time)
      headers: {
        'Content-Type': headers['Content-Type'] || 'application/json',
      },
      user_id: user?.id || null,
      tenant_id: user?.tenant_id || null,
      status: QueueStatus.PENDING,
      attempts: 0,
      last_error: null,
      action_label: this.getActionLabel(url, method),
    };
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(item);
      
      request.onsuccess = () => {
        this.notifyListeners();
        resolve(item);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get all queued items for current user
  async getAllItems() {
    if (!this.db) await this.openDatabase();
    
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      
      request.onsuccess = () => {
        // Filter by user/tenant
        let items = request.result || [];
        if (user) {
          items = items.filter(item => 
            item.user_id === user.id || item.tenant_id === user.tenant_id
          );
        }
        // Sort by created_at (FIFO)
        items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Update item status
  async updateItem(id, updates) {
    if (!this.db) await this.openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const item = { ...getRequest.result, ...updates };
        const putRequest = store.put(item);
        putRequest.onsuccess = () => {
          this.notifyListeners();
          resolve(item);
        };
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Remove item from queue - ONLY call on successful sync
  async removeItem(id) {
    if (!this.db) await this.openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Clear all items (with confirmation from UI) - manual user action only
  async clearAll() {
    if (!this.db) await this.openDatabase();
    
    const items = await this.getAllItems();
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Only clear items for current user
      let deleted = 0;
      items.forEach(item => {
        if (!user || item.user_id === user.id || item.tenant_id === user.tenant_id) {
          store.delete(item.id);
          deleted++;
        }
      });
      
      transaction.oncomplete = () => {
        this.lastSyncError = null;
        this.loginRequired = false;
        this.notifyListeners();
        resolve(deleted);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Sync queue - replay pending actions
  async syncQueue() {
    if (this.isSyncing || !this.isOnline) return;
    
    // Check if login is required before attempting sync
    const token = localStorage.getItem('access_token');
    if (!token) {
      this.loginRequired = true;
      this.lastSyncError = 'Login required to sync pending actions';
      this.notifyListeners();
      return;
    }
    
    this.isSyncing = true;
    this.lastSyncError = null;
    this.loginRequired = false;
    this.notifyListeners();
    
    let syncedCount = 0;
    let failedCount = 0;
    
    try {
      const items = await this.getAllItems();
      const pendingItems = items.filter(i => i.status === QueueStatus.PENDING || i.status === QueueStatus.SYNCING);
      
      for (const item of pendingItems) {
        // Check if still online
        if (!this.isOnline) {
          this.lastSyncError = 'Network connection lost';
          break;
        }
        
        // Check if login became required
        if (this.loginRequired) {
          break;
        }
        
        const success = await this.syncItem(item);
        if (success) {
          syncedCount++;
        } else {
          failedCount++;
        }
      }
      
      if (failedCount > 0 && !this.lastSyncError) {
        this.lastSyncError = `${failedCount} action(s) failed to sync`;
      }
    } catch (error) {
      this.lastSyncError = error.message || 'Sync failed unexpectedly';
    } finally {
      this.isSyncing = false;
      this.backoffDelay = 5000; // Reset backoff on completion
      this.notifyListeners();
    }
  }

  // Sync single item
  async syncItem(item) {
    // Get current token
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      // Token expired/missing - pause sync, keep item in queue
      this.loginRequired = true;
      this.lastSyncError = 'Login required to sync pending actions';
      await this.updateItem(item.id, {
        status: QueueStatus.PENDING,
        last_error: 'Login required to sync',
      });
      return false;
    }
    
    // Mark as syncing
    await this.updateItem(item.id, { status: QueueStatus.SYNCING, last_error: null });
    
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: {
          ...item.headers,
          'Authorization': `Bearer ${token}`,
        },
        body: item.body ? JSON.stringify(item.body) : undefined,
      });
      
      if (response.ok) {
        // SUCCESS (2xx) - ONLY now remove from queue
        await this.removeItem(item.id);
        return true;
      } else if (response.status === 401) {
        // Token expired - try refresh
        const refreshed = await this.attemptTokenRefresh();
        if (refreshed) {
          // Retry with new token
          return this.syncItem(item);
        } else {
          // Refresh failed - pause sync, keep item in queue
          this.loginRequired = true;
          this.lastSyncError = 'Login required to sync pending actions';
          await this.updateItem(item.id, {
            status: QueueStatus.PENDING,
            last_error: 'Login required to sync',
          });
          return false;
        }
      } else if (response.status >= 400 && response.status < 500) {
        // 4xx validation error - mark as failed (user needs to review)
        const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
        await this.updateItem(item.id, {
          status: QueueStatus.FAILED,
          attempts: item.attempts + 1,
          last_error: errorData.detail || `Error ${response.status}`,
        });
        return false;
      } else {
        // 5xx server error - keep as pending, will retry
        await this.updateItem(item.id, {
          status: QueueStatus.PENDING,
          attempts: item.attempts + 1,
          last_error: `Server error (${response.status}) - will retry`,
        });
        this.lastSyncError = `Server error (${response.status})`;
        return false;
      }
    } catch (error) {
      // Network error - keep as pending, will retry
      await this.updateItem(item.id, {
        status: QueueStatus.PENDING,
        attempts: item.attempts + 1,
        last_error: error.message || 'Network error - will retry',
      });
      this.lastSyncError = error.message || 'Network error';
      return false;
    }
  }

  async attemptTokenRefresh() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
    
    return false;
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Manual retry trigger - "Sync now" button
  async retryNow() {
    // Reset failed items to pending so they get retried
    const items = await this.getAllItems();
    for (const item of items) {
      if (item.status === QueueStatus.FAILED) {
        await this.updateItem(item.id, { status: QueueStatus.PENDING, last_error: null });
      }
    }
    
    // Clear error states
    this.lastSyncError = null;
    this.loginRequired = false;
    
    // Trigger sync
    return this.syncQueue();
  }
}

// Singleton instance
export const offlineQueue = new OfflineQueueManager();
export default offlineQueue;
